import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import cn from 'classnames';
import {TableCell} from '@material-ui/core';
import ReactPaginate from 'react-paginate';
import {DashboardContent} from 'components/DashboardContent/styles';
import {DashboardHeader} from 'components/DashboardHeader/styles';
import {FlexContainer, FlexItem, MainPaper} from 'components/layout';
import SearchField from 'components/fields/SearchField';
import {ButtonOutline, ButtonPrimary} from 'components/buttons';
import IconAdd from 'assets/IconAdd';
import Headline from 'components/headline2/HeadlineComponent';
import Loading from 'components/shared/Utils/Loading';
import Table from 'components/Table';
import ConfirmModal from 'components/ModalBox/ConfirmModal';
import ButtonMore from 'routes/App/merchants/components/ButtonMore';
import {
  TablePagination,
  TablePaginationText
} from 'routes/App/dashboard/styles';
import {
  CLEAR_FILTERS,
  CREATE_BYOC_VIRTUAL_NUMBER,
  DELETE_BYOC_VIRTUAL_NUMBER,
  GET_BYOC_VIRTUAL_NUMBERS,
  PAGE_CHANGE,
  SEARCH_CHANGE,
  UPDATE_BYOC_VIRTUAL_NUMBER
} from 'store/byocVirtualNumbers/action';
import ByocVirtualNumberModal from 'routes/App/Managers/ByocVirtualNumbers/Modals/ByocVirtualNumberModal';

const formatBrand = (item) => {
  if (!item?.brandId && !item?.brandName) {
    return '-';
  }
  if (item.brandName && item.brandId) {
    return `${item.brandName} (${item.brandId})`;
  }
  return item.brandName || item.brandId;
};

const getTableModel = (onEditClick, onDeleteClick) => [
  {
    title: 'Location name',
    width: '22%',
    renderer: (item) => <TableCell>{item?.locationName || '-'}</TableCell>
  },
  {
    title: 'Location ID',
    width: '22%',
    renderer: (item) => <TableCell>{item?.locationId}</TableCell>
  },
  {
    title: 'Brand',
    width: '18%',
    renderer: (item) => <TableCell>{formatBrand(item)}</TableCell>
  },
  {
    title: 'Phone',
    width: '15%',
    renderer: (item) => <TableCell>{item?.twilioPhone || '-'}</TableCell>
  },
  {
    title: 'Twilio SID',
    width: '15%',
    renderer: (item) => <TableCell>{item?.twilioPhoneSid || '-'}</TableCell>
  },
  {
    width: '8%',
    renderer: (item) => (
      <TableCell>
        <ButtonMore
          itemsData={[
            {
              title: 'Edit',
              action: () => onEditClick(item)
            },
            {
              title: 'Delete',
              action: () => onDeleteClick(item)
            }
          ]}
        />
      </TableCell>
    )
  }
];

const ByocVirtualNumbers = () => {
  const dispatch = useDispatch();
  const {
    loading,
    deleting,
    rows,
    paging: {total, pageSize, pages, page}
  } = useSelector((state) => state.byocVirtualNumbersReducer);

  const [searchField, setSearchField] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(CLEAR_FILTERS());
  }, []);

  useEffect(() => {
    dispatch(GET_BYOC_VIRTUAL_NUMBERS({page, search: searchField}));
  }, [page]);

  const onPageChange = (newPage) => {
    dispatch(PAGE_CHANGE(newPage.selected));
  };

  const onSearchClick = () => {
    dispatch(SEARCH_CHANGE(searchField));
    dispatch(GET_BYOC_VIRTUAL_NUMBERS({page: 0, search: searchField}));
  };

  const onCreate = ({locationId, brandId, brandName}) => {
    dispatch(
      CREATE_BYOC_VIRTUAL_NUMBER({
        locationId,
        brandId,
        brandName,
        onSuccess: () => setIsCreateModalOpen(false)
      })
    );
  };

  const onUpdate = ({locationId, brandId, brandName}) => {
    if (!editTarget?.id) {
      return;
    }

    dispatch(
      UPDATE_BYOC_VIRTUAL_NUMBER({
        id: editTarget.id,
        locationId,
        brandId,
        brandName,
        onSuccess: () => setEditTarget(null)
      })
    );
  };

  const onDeleteConfirm = () => {
    if (!deleteTarget?.id) {
      return;
    }

    dispatch(
      DELETE_BYOC_VIRTUAL_NUMBER({
        id: deleteTarget.id,
        onSuccess: () => setDeleteTarget(null)
      })
    );
  };

  const showingCount = pageSize > total ? total : pageSize;

  return (
    <DashboardContent className='DashboardContent overflowInherit DashboardContentWithoutHeader BgGrey'>
      <DashboardHeader>
        <Headline label='BYOC VA Numbers' />
        <ButtonPrimary
          className='ButtonAdd ButtonMedium'
          onClick={() => setIsCreateModalOpen(true)}
        >
          <IconAdd />
          <span>Add new</span>
        </ButtonPrimary>
      </DashboardHeader>

      <FlexContainer className='mTop20'>
        <FlexItem flex='1 1 auto' className='marginRightXs'>
          <SearchField
            className='marginBottomXs'
            placeholder='Filter by location or brand name'
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSearchClick()}
          />
        </FlexItem>
        <FlexItem>
          <ButtonOutline className={cn('ButtonMedium')} onClick={onSearchClick}>
            Search
          </ButtonOutline>
        </FlexItem>
      </FlexContainer>

      {loading ? (
        <Loading />
      ) : (
        <MainPaper>
          {total ? (
            <Table
              tableModel={getTableModel(setEditTarget, setDeleteTarget)}
              data={rows}
              rowKey='id'
            />
          ) : (
            <div>There are no BYOC virtual numbers to display</div>
          )}

          <TablePagination>
            <TablePaginationText>
              Showing {showingCount} out of {total} data
            </TablePaginationText>
            <ReactPaginate
              previousLabel='previous'
              nextLabel='next'
              breakLabel='...'
              breakClassName='break-me'
              pageCount={pages}
              initialPage={page}
              forcePage={page}
              marginPagesDisplayed={2}
              pageRangeDisplayed={5}
              onPageChange={onPageChange}
              containerClassName='pagination'
              subContainerClassName='pages pagination'
              activeClassName='active'
            />
          </TablePagination>
        </MainPaper>
      )}

      {isCreateModalOpen && (
        <ByocVirtualNumberModal
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={onCreate}
        />
      )}

      {editTarget && (
        <ByocVirtualNumberModal
          number={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={onUpdate}
        />
      )}

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        isLoading={deleting}
        message={`Are you sure you want to delete the BYOC virtual number for ${
          deleteTarget?.locationName || 'this location'
        }?`}
        btnOkTitle='Delete'
        btnCancelTitle='Cancel'
        onCancel={() => setDeleteTarget(null)}
        onOk={onDeleteConfirm}
      />
    </DashboardContent>
  );
};

export default ByocVirtualNumbers;
